import { definePlugin } from "prosekit/core";
import { Plugin, PluginKey } from "prosekit/pm/state";

export function removeImagesFromHTML(html: string): string {
  return html.replace(/<img[^>]*>/gi, "");
}

/**
 * Prevents pasting images into the editor to avoid confusion.
 * Images are rendered in the editor but dropped during markdown
 * serialization, leaving empty paragraphs in the saved content.
 * Users should use markdown syntax ![alt](url) or file attachments instead.
 */
export function defineBlockImagePaste() {
  return definePlugin(
    new Plugin({
      key: new PluginKey("prosekit-block-image-paste"),
      props: {
        handlePaste: (_, event) => {
          // Block pasting of image files directly
          const items = event.clipboardData?.items;
          if (!items) return false;

          let hasImage = false;
          let hasHTML = false;
          let hasText = false;

          for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item.type.indexOf("image") !== -1) hasImage = true;
            if (item.type.indexOf("html") !== -1) hasHTML = true;
            if (item.type.indexOf("text") !== -1) hasText = true;
          }

          // Only block if there's an image file WITHOUT accompanying HTML/text
          // This allows Word/HTML pastes that contain embedded images to proceed
          if (hasImage && !hasHTML && !hasText) {
            event.preventDefault();
            return true; // Handled (blocked)
          }

          return false; // Not handled, let other handlers process
        },
        // Strip <img> tags from pasted HTML (e.g. from Word). Otherwise they
        // are shown in the editor and later disappear during markdown
        // serialization, leaving empty paragraphs.
        transformPastedHTML: (html) => removeImagesFromHTML(html),
      },
    }),
  );
}
